import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core'
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  MatNativeDateModule,
} from '@angular/material/core'
import {
  DateRange,
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendarCell,
  MatCalendarUserEvent,
  MatDatepickerModule,
  MatDateRangeSelectionStrategy,
} from '@angular/material/datepicker'
import { UntilDestroy } from '@ngneat/until-destroy'
import { DateFilter } from '@wm-front/admin/graphql'
import { IconButtonComponent } from '@wm-front/shared/buttons'
import dayJS from 'dayjs'
import {
  DayjsDateAdapter,
  MAT_DAYJS_DATE_ADAPTER_OPTIONS,
} from '../../helpers/date-adapter.class'
import { TrackChanges } from '@wm-front/shared/utils'
import { NgClickOutsideDelayOutsideDirective } from 'ng-click-outside2'

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
}

@UntilDestroy()
@Component({
  selector: 'wm-front-month-range-picker',
  templateUrl: './month-range-picker.component.html',
  styleUrls: ['./month-range-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
    {
      provide: DateAdapter,
      useClass: DayjsDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  imports: [
    NgClickOutsideDelayOutsideDirective,
    MatDatepickerModule,
    MatNativeDateModule,
    IconButtonComponent,
  ],
})
export class MonthRangePickerComponent implements OnInit, OnChanges {
  /** Emits when value changes */
  @Input() public period: DateFilter

  /** Override the activation of reset button */
  @Input() public activeResetBtn = false

  /** Minimum date validation */
  @Input() public minDate: string

  /** Maximum date validation */
  @Input() public maxDate: string

  /** Minimim range of months to be selected, else an error will pop */
  @Input() public minRange: number

  /** Emits when value changes */
  @Output() public valueChanged: EventEmitter<DateFilter> = new EventEmitter()

  /** Emits when value reset */
  @Output() public reseted: EventEmitter<void> = new EventEmitter()

  /** Emits when it should close */
  @Output() public shouldClose: EventEmitter<void> = new EventEmitter()

  /** Selected range values */
  public selectedRangeValue: DateRange<dayJS.Dayjs> =
    new DateRange<dayJS.Dayjs>(null, null)

  /** The months of the year for generating the calendar body */
  public months: MatCalendarCell[][] = []

  public dateAdapter = inject<DateAdapter<dayJS.Dayjs>>(DateAdapter)

  /** The active date, today by default */
  public activeDate: dayJS.Dayjs = this.dateAdapter.today()

  /** The active year in the calendar header */
  public activeYear: number = this.dateAdapter.getYear(this.activeDate)

  /** Start of the preview range. */
  public previewStart: dayJS.Dayjs | null

  /** End of the preview range. */
  public previewEnd: dayJS.Dayjs | null

  /** Show min range error */
  public showMinRangeError: boolean

  protected _cd = inject(ChangeDetectorRef)

  protected _dateFormats = inject<MatDateFormats>(MAT_DATE_FORMATS)

  protected _rangeStrategy = inject<MatDateRangeSelectionStrategy<dayJS.Dayjs>>(
    MAT_DATE_RANGE_SELECTION_STRATEGY
  )

  /** The value used if the start date is before the months displayed */
  private readonly valueBeforeMinMonth = -1

  /** The value used if the end date is after the months displayed */
  private readonly valuePastMaxMonth = 12

  /** Min date as dayJS object
   * @returns { dayJS.Dayjs }
   */
  public get minDateMoment(): dayJS.Dayjs {
    return dayJS(this.minDate)
  }

  /** Max date as dayJS object
   * @returns { dayJS.Dayjs }
   */
  public get maxDateMoment(): dayJS.Dayjs {
    return dayJS(this.maxDate)
  }

  public ngOnInit(): void {
    this.generateMonths()
  }

  /* eslint-disable */
  @TrackChanges('period', 'handlePeriodChanges')
  public ngOnChanges(changes: SimpleChanges): void {}

  /* eslint-enable */

  /** Reset period filter */
  public resetPeriod(): void {
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(null, null)
    this.showMinRangeError = false
    this.reseted.emit()
  }

  /** Go to next year */
  public nextYear(): void {
    this.activeYear = this.activeYear + 1
    this.generateMonths()
  }

  /** Go to previous year */
  public previousYear(): void {
    this.activeYear = this.activeYear - 1
    this.generateMonths()
  }

  /** Update range values depending on whether it's start or end of the range
   * @param { MatCalendarUserEvent<number> }
   */
  public selectedChange(event: MatCalendarUserEvent<number>): void {
    const month = event.value

    const selectedMonth = this.dateAdapter.createDate(this.activeYear, month, 1)

    this.activeDate = selectedMonth
    this._cd.markForCheck()

    if (
      !this.selectedRangeValue?.start ||
      (this.selectedRangeValue?.start && this.selectedRangeValue?.end)
    ) {
      this.selectedRangeValue = new DateRange<dayJS.Dayjs>(selectedMonth, null)
      this.previewStart = selectedMonth
    } else {
      if (selectedMonth.isAfter(this.selectedRangeValue.start)) {
        const start = this.selectedRangeValue.start
        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
          start,
          dayJS(selectedMonth).endOf('M')
        )
      } else if (selectedMonth.isSame(this.selectedRangeValue.start)) {
        const end = dayJS(selectedMonth).endOf('M')

        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(selectedMonth, end)
      } else {
        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
          selectedMonth,
          null
        )
      }

      if (
        (this.selectedRangeValue.end?.diff(
          this.selectedRangeValue.start,
          'M'
        ) as number) +
          1 <
        this.minRange
      ) {
        this.showMinRangeError = true
        this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
          this.selectedRangeValue.start,
          null
        )
        return
      }

      this.valueChanged.emit({
        after: this.selectedRangeValue.start?.toISOString(),
        before: this.selectedRangeValue.end?.toISOString(),
      })

      this.showMinRangeError = false
      this.previewStart = null
      this.previewEnd = null
    }
  }

  /** Called when the user has activated a new cell and the preview needs to be updated.
   * @param { number }
   * @param { MatCalendarCell<dayJS.Dayjs> }
   * @returns { MatCalendarCell<dayJS.Dayjs> }
   */
  public previewChanged({
    event,
    value: cell,
  }: MatCalendarUserEvent<MatCalendarCell<dayJS.Dayjs> | null>) {
    const date = this.dateAdapter.createDate(
      this.activeYear,
      cell?.value as number,
      1
    )
    const previewRange = this._rangeStrategy.createPreview(
      date,
      this.selectedRangeValue,
      event
    )
    this.previewStart = previewRange.start || null
    this.previewEnd = previewRange.end || null
    this._cd.detectChanges()
  }

  /**
   * Will return the value from the provided date
   * @param {dayjs.Dayjs} date
   * @returns {number | null}
   */
  public getValueFromDate(date: dayJS.Dayjs | null): number | null {
    if (date && this.dateAdapter.getYear(date) === this.activeYear) {
      return this.dateAdapter.getMonth(date)
    }
    if (date && this.dateAdapter.getYear(date) < this.activeYear) {
      return this.valueBeforeMinMonth
    }
    if (date && this.dateAdapter.getYear(date) > this.activeYear) {
      return this.valuePastMaxMonth
    }
    return null
  }

  /** Render months on calendar body */
  private generateMonths(): void {
    const monthNames = this.dateAdapter.getMonthNames('short')

    this.months = [
      [0, 1, 2, 3],
      [4, 5, 6, 7],
      [8, 9, 10, 11],
    ].map((row) =>
      row.map((month) => this.createCellForMonth(month, monthNames[month]))
    )
  }

  /** Creates an MatCalendarCell for the given month.
   * @param { number }
   * @param { string }
   * @returns { MatCalendarCell<dayJS.Dayjs> }
   */
  private createCellForMonth(
    month: number,
    monthName: string
  ): MatCalendarCell<dayJS.Dayjs> {
    const date = this.dateAdapter.createDate(this.activeYear, month, 1)

    const ariaLabel = this.dateAdapter.format(
      date,
      this._dateFormats.display.monthYearA11yLabel
    )

    return new MatCalendarCell(
      month,
      monthName.charAt(0).toUpperCase() + monthName.slice(1),
      ariaLabel,
      this.shouldEnableMonth(month),
      undefined,
      undefined,
      date
    )
  }

  /** Handle changes in period
   * @param { DateFilter }
   */
  private handlePeriodChanges(period: DateFilter): void {
    this.selectedRangeValue = new DateRange<dayJS.Dayjs>(
      dayJS(period.after),
      dayJS(period.before)
    )
  }

  /** Whether the given month is enabled.
   * @param { number }
   */
  private shouldEnableMonth(month: number): boolean {
    if (
      month === undefined ||
      month === null ||
      this.isYearAndMonthAfterMaxDate(this.activeYear, month) ||
      this.isYearAndMonthBeforeMinDate(this.activeYear, month)
    ) {
      return false
    }

    return true
  }

  /**
   * Tests whether the combination month/year is after this.maxDate, considering
   * just the month and year of this.maxDate
   * @param { number }
   * @param { number }
   * @returns { boolean }
   */
  private isYearAndMonthAfterMaxDate(year: number, month: number): boolean {
    if (this.maxDate) {
      const maxYear = this.dateAdapter.getYear(this.maxDateMoment)
      const maxMonth = this.dateAdapter.getMonth(this.maxDateMoment)

      return year > maxYear || (year === maxYear && month > maxMonth)
    }

    return false
  }

  /**
   * Tests whether the combination month/year is before this.minDate, considering
   * just the month and year of this.minDate
   * @param { number }
   * @param { number }
   * @returns { boolean }
   */
  private isYearAndMonthBeforeMinDate(year: number, month: number): boolean {
    if (this.minDate) {
      const minYear = this.dateAdapter.getYear(this.minDateMoment)
      const minMonth = this.dateAdapter.getMonth(this.minDateMoment)

      return year < minYear || (year === minYear && month < minMonth)
    }

    return false
  }
}
